'use client';

import type { INavListItem } from '@/api/types/public';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { EPlatform } from '@/utils/enums/platform-enum';
import { buildSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import NextImage from 'next/image';
import styles from './index.module.scss';
import { buildSubmenuSpmPosition } from './submenuSpm';

interface ResourceSlotNavProps {
  item: INavListItem;
  firstLevelIndex: number;
  firstLevelTitle: string;
  resourceIndex: number;
  /** 扁平坑位序号偏移：本资源位前所有占号区块的累计数；资源位固定占 1 号（含无跳转的）。 */
  spmItemOffset?: number;
}

const DEFAULT_TITLE_COLOR = '#FFFFFF';
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export function getResourceSlotTitleColor(color?: string) {
  return color && HEX_COLOR_PATTERN.test(color) ? color : DEFAULT_TITLE_COLOR;
}

const ResourceSlotNav = ({
  item,
  firstLevelIndex,
  firstLevelTitle,
  resourceIndex,
  spmItemOffset = 0,
}: ResourceSlotNavProps) => {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  // 全局组件的 B 位跟随当前页面，C/D 沿用全局终端模板。
  const spmPage = useGlobalSpmTerminal(EPlatform.WEB);
  const content = item.content;
  const hasJumpTarget =
    content?.Jump_target !== undefined &&
    content?.Jump_target !== null &&
    String(content?.Jump_target).trim() !== '';
  // 资源位固定占一个坑位号（submenu_item_GG_II 的 II = 偏移 + 1）；无跳转不赋码，坑位号照常 +1。
  const resourceSlotSpmCnt = buildSpmPositionCode(
    spmPage,
    'primary_navigation',
    buildSubmenuSpmPosition(firstLevelIndex, spmItemOffset),
  );
  const resourceSlotDomSpm = useSpmDomAttr(
    hasJumpTarget && Number(content?.jump_type) !== 8 ? resourceSlotSpmCnt : undefined,
  );

  if (!content?.resource_image) return null;

  const title = content.resource_title || '';
  const hasTitle = title.trim() !== '';
  const jumpTarget = {
    jump_type: content.jump_type ?? 0,
    Jump_target: content.Jump_target ?? '',
    product_group: {
      id: content.product_group?.id,
      path: content.product_group?.path,
    },
    sku: {
      shop_sku: Array.isArray(content.sku) ? content.sku[0] : content.sku?.shop_sku || '',
    },
  };

  const handleBeforeJump = () => {
    setSession('zl_goodsListEnterPageScene', '全局导航');
    setCampaignEntrancePageScene('顶部导航', content.Jump_target);
    trackEvent(
      {
        eventName: 'TopNavigationMkt_Click',
        properties: withSpmCnt(
          {
            page_name: cnName,
            url_path: window.location.href,
            tab_name: firstLevelTitle,
            tab_index: firstLevelIndex,
            pits_index: resourceIndex,
            pits_title: title,
          },
          resourceSlotSpmCnt,
        ),
      },
      [ProviderType.SENSORS],
    );
  };

  const resourceSlotClassName = `relative block shrink-0 self-stretch overflow-hidden ${styles.resourceSlotItem} ${
    hasJumpTarget ? 'group cursor-pointer' : ''
  }`;
  const resourceSlotContent = (
    <>
      <Image
        src={content.resource_image}
        alt={title}
        fill
        sizes="(max-width: 1919px) 415px, 464px"
        className={`object-cover ${
          hasJumpTarget ? 'transition-transform duration-300 group-hover:scale-[1.02]' : ''
        }`}
      />
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-[8px] bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.4)] p-[16px]">
        {hasTitle ? (
          <span
            className="min-w-0 truncate font-bb text-[16px] leading-[19px]"
            style={{ color: getResourceSlotTitleColor(content.resource_title_color) }}
          >
            {title}
          </span>
        ) : null}
        {hasJumpTarget && hasTitle ? (
          <NextImage
            src="/zeelool-next/pc-navigation/arrow.svg"
            alt=""
            width={24}
            height={24}
            className="size-[24px] shrink-0"
            unoptimized
            aria-hidden
          />
        ) : null}
      </div>
    </>
  );

  if (!hasJumpTarget) {
    return <div className={resourceSlotClassName}>{resourceSlotContent}</div>;
  }

  return (
    <MagicJump
      className={resourceSlotClassName}
      item={jumpTarget}
      onBeforeJump={handleBeforeJump}
      {...resourceSlotDomSpm}
    >
      {resourceSlotContent}
    </MagicJump>
  );
};

export default ResourceSlotNav;
